import React, { useRef, useState } from 'react';
import { Input, Typography, Layout, Tooltip, message, Popover, Divider } from 'antd';
import { SearchOutlined, CloudDownloadOutlined, BarChartOutlined, ReloadOutlined, CloudOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import DB from '../services/dbService';
import UsageStats from './UsageStats';
import '../styles/data-center.css';
import '../styles/upgrade-alert.css';

const { Header: AntHeader } = Layout;
const { Title } = Typography;

// 顶部导航栏组件，包含应用标题和搜索框
function Header({ searchTerm, onSearchChange, onSearch, onCloudBackup }) {
  // 控制升级提示的显示状态
  const [showUpgradeAlert, setShowUpgradeAlert] = useState(true);
  // 获取当前路径，用于判断是否在应用页面
  const location = useLocation();
  const isAppPage = location.pathname === '/app';
  
  // 处理一键备份功能
  const handleBackupAll = async () => {
    try {
      // 显示加载中消息
      message.loading({ content: '正在准备备份数据...', key: 'backup' });
      
      // 使用dbService获取数据
      try {
        // 获取所有数据
        const prompts = await DB.getAllItems(DB.STORES.PROMPTS);
        const folders = await DB.getAllItems(DB.STORES.FOLDERS);
        const tags = await DB.getAllItems(DB.STORES.TAGS);
        
        // 更新消息
        message.loading({ content: `正在导出 ${prompts.length} 个提示词...`, key: 'backup' });
        
        // 创建导出数据
        // 处理提示词数据，确保每个版本的结果图片被正确包含
        const processedPrompts = prompts.map(prompt => {
          // 确保每个版本的结果图片被包含
          const processedVersions = prompt.versions.map(version => {
            // 如果版本包含结果图片，确保完整导出
            return {
              ...version,
              resultImages: version.resultImages || []
            };
          });
          
          return {
            ...prompt,
            versions: processedVersions
          };
        });
        
        const exportData = {
          version: '1.1', // 更新版本号，表示支持图片导出
          exportedAt: new Date().toISOString(),
          prompts: processedPrompts,
          folders: folders,
          tags: tags,
          includesImages: true // 标记此备份包含图片数据
        };
        
        // 创建并下载JSON文件
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `PromptPro_完整备份_${new Date().toLocaleDateString().replace(/\//g, '-')}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // 显示成功消息
        message.success({ content: '备份成功！文件已下载', key: 'backup', duration: 3 });
      } catch (error) {
        console.error('备份数据失败:', error);
        message.error({ content: '备份数据失败，请稍后再试', key: 'backup', duration: 3 });
      }
    } catch (error) {
      console.error('备份过程中发生错误:', error);
      message.error({ content: '备份过程中发生错误，请稍后再试', key: 'backup', duration: 3 });
    }
  };

  // 搜索框变化处理
  const handleSearchChange = (e) => {
    if (onSearchChange) {
      onSearchChange(e.target.value);
    }
    if (onSearch) {
      onSearch(e.target.value);
    }
  };

  // 数据中心内容
  const usageStatsRef = useRef(null);
  
  const handleRefreshData = () => {
    if (usageStatsRef.current) {
      usageStatsRef.current.refreshData();
    }
  };
  
  const dataCenterContent = (
    <div className="w-96 max-h-[80vh] overflow-y-auto custom-scrollbar">
      <div className="text-center mb-3">
        <Title level={4} className="m-0 font-semibold bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">
          数据中心
        </Title>
        <div className="text-xs text-gray-500">全面的数据分析与统计</div>
      </div>
      
      <div className="mb-4">
        <UsageStats ref={usageStatsRef} />
      </div>
      
      <Divider className="my-3" />
      
      <div className="text-center">
        <button 
          onClick={handleRefreshData} 
          className="refresh-button flex items-center mx-auto"
        >
          <ReloadOutlined style={{ marginRight: '4px' }} />
          刷新数据
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* PromptPro 2.0 升级提示 */}
      {showUpgradeAlert && (
        <div className="w-full bg-gradient-to-r from-blue-600 to-indigo-500 text-white py-3 px-4 flex items-center justify-center relative upgrade-alert">
          <div className="flex items-center cursor-pointer" onClick={() => window.open('https://vip.pmpro.top', '_blank')}>
            <span className="font-medium mr-3 text-lg upgrade-text">🎉 PromptPro 2.0 重构版上线</span>
            <button className="bg-white text-blue-600 text-sm px-4 py-1.5 rounded-full flex items-center hover:bg-blue-50 transition-colors duration-300 font-medium upgrade-button shadow-md">
              点击迁移使用 <ArrowRightOutlined style={{ marginLeft: '6px' }} />
            </button>
          </div>
          <button 
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white opacity-80 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              setShowUpgradeAlert(false);
            }}
          >
            ×
          </button>
        </div>
      )}
      <AntHeader className="header-modern glassmorphism sticky top-0 z-50 h-auto backdrop-blur-md border-b border-[var(--apple-border-light)]">
      <div className="flex items-center justify-between py-3 w-full">
        {/* 应用标题和LOGO */}
        <div className="flex items-center">
          <div className="relative group">
            <svg t="1742008091301" className="icon w-9 h-9 transition-transform duration-300 group-hover:scale-110" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2820" width="36" height="36">
              <path d="M514.29 257.53c137.98 1.23 249.48 113.46 249.48 251.75 0 44.76-11.69 86.81-32.16 123.22-41.8 74.32-120.23 125.29-210.8 128.39-1.01 0.04-2.01 0.06-3.02 0.07-0.66 0.01-1.32 0.02-1.96 0.04-1.27 0.02-2.55 0.04-3.82 0.04-53 0-96.35-43.35-96.35-96.35 0-24.63 9.36-47.18 24.7-64.25 1.17-1.29 2.36-2.56 3.59-3.79 17.47-17.47 41.56-28.31 68.06-28.31 85.82 0 155.41-69.57 155.41-155.41-0.01-85.07-68.37-154.17-153.13-155.4z" fill="#4770DC" p-id="2821"></path>
              <path d="M608.35 161.16c139.04 0 251.76 112.72 251.76 251.76 0 1.27-0.01 2.55-0.04 3.82-0.01 0.65-0.02 1.31-0.04 1.96-0.01 1.01-0.04 2.01-0.07 3.02-4.58 185.27-153.9 334.58-339.16 339.17 90.57-3.11 169-54.07 210.8-128.39 20.48-36.42 32.16-78.46 32.16-123.22 0-138.29-111.5-250.52-249.48-251.75-0.77-0.01-1.53-0.01-2.29-0.01-1.34 0-2.67 0.01-4.02 0.05-82.64 2.1-149.26 68.72-151.34 151.34 0.66-43.27 12.25-83.88 32.13-119.22 43.14-76.71 125.32-128.53 219.59-128.53z" fill="#6F94F4" p-id="2822"></path>
              <path d="M512 64.8c192.26 0 348.12 155.86 348.12 348.12 0-139.05-112.73-251.76-251.76-251.76-94.28 0-176.46 51.82-219.59 128.53-19.88 35.33-31.47 75.95-32.13 119.22-0.04 1.34-0.05 2.67-0.05 4.02v449.92c0 53-43.37 96.35-96.35 96.35-53 0-96.35-43.35-96.35-96.35V412.92c0-136.41 78.45-254.49 192.71-311.59C403.36 77.96 456.15 64.8 512 64.8z" fill="#97B4FF" p-id="2823"></path>
            </svg>
            <div className="absolute -inset-1 bg-blue-50 rounded-full blur opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
          </div>
          <div className="ml-3">
            <Title level={4} className="m-0 font-semibold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent" style={{ letterSpacing: '-0.02em' }}>PromptPro</Title>
            <div className="text-xs text-[var(--apple-text-secondary)] -mt-1">产品化管理提示词</div>
          </div>
        </div>
        
        {/* 移除中间区域的导航菜单 */}
        
        {/* 搜索栏和操作按钮 */}
        <div className="flex items-center justify-between flex-1 ml-8">
          <div className="w-64">
            <Input
              prefix={<SearchOutlined className="text-[var(--apple-icon)]" />}
              placeholder="搜索提示词..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="input-modern rounded-full shadow-soft hover:shadow-medium transition-all"
              size="large"
              allowClear
            />
          </div>
          
          <div className="flex items-center space-x-3">
            {/* 数据中心按钮 */}
            {isAppPage && (
              <Popover 
                content={dataCenterContent} 
                title={null} 
                trigger="click" 
                placement="bottomRight"
                overlayClassName="data-center-popover"
                overlayStyle={{ maxWidth: '90vw' }}
              >
                <Tooltip title="数据中心">
                  <button 
                    className="flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-400 text-white hover:shadow-lg hover:scale-105 transition-all duration-300 ease-in-out"
                  >
                    <BarChartOutlined style={{ fontSize: '18px', marginRight: '6px' }} />
                    <span className="text-sm font-medium">数据中心</span>
                  </button>
                </Tooltip>
              </Popover>
            )}
            
            {/* 云端备份按钮 */}
            {isAppPage && (
              <Tooltip title="云端备份与恢复">
                <button 
                  className="flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-500 to-teal-400 text-white hover:shadow-lg hover:scale-105 transition-all duration-300 ease-in-out"
                  onClick={onCloudBackup}
                >
                  <CloudOutlined style={{ fontSize: '18px', marginRight: '6px' }} />
                  <span className="text-sm font-medium">云端备份</span>
                </button>
              </Tooltip>
            )}
            
            {/* 只在应用页面显示备份按钮 */}
            {isAppPage && (
              <Tooltip title="一键备份到本地">
                <button 
                  className="flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 text-white hover:shadow-lg hover:scale-105 transition-all duration-300 ease-in-out"
                  onClick={handleBackupAll}
                >
                  <CloudDownloadOutlined style={{ fontSize: '18px', marginRight: '6px' }} />
                  <span className="text-sm font-medium">本地备份</span>
                </button>
              </Tooltip>
            )}
            
            {/* 移除帮助中心按钮和通知按钮 */}
            
            {/* 移除用户头像 */}
          </div>
        </div>
      </div>
    </AntHeader>
    </>
  );
}

export default Header;
